import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import styles from "./details.module.css";
import PageHeader from "../../../components/PageHeader";
import Otp from "../../common/Otp";
import Illustration from "../../../assets/images/customer-illustration.svg";
import { getOTP, searchAwb, verifyOtpActionForEdit } from "../../../utils/api";
import { getAWBFailure, getAWBSuccess } from "../../../store/actions/awb";
import { initiateApiCall, setCompanyInfo } from "../../../store/actions/companyInfo";
import { track, trackError } from "../../../analytics";
import usePageView from "../../../analytics/usePageView";
import { ensureOtpSessionFor } from "../../../utils/otpSession";

// Customer Details page (/:awb/showdetails), ported from the Claude Design file
// "Customer Details.dc.html". View only: the details come from the order
// (logistic /cod/details/tracking_page through full-details) and cannot be edited here.

const WHATSAPP_URL = "https://api.whatsapp.com/send?phone=919606097301";

// Manage Order saves "<flat> | <Google address>"; show it as one readable line.
const readableAddress = (shipping) =>
  [shipping?.address, shipping?.address1]
    .filter(Boolean)
    .join(" ")
    .replace(/\s*\|\s*/g, ", ")
    .trim();

// "+91 ••••• •8674" for a real number. The API masks the phone ("****"), so most of the time
// only a fully masked value can be shown.
const displayPhone = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const digits = raw.replace(/\D/g, "");
  if (digits.length >= 10 && !/[a-z*]/i.test(raw)) return `+91 ••••• •${digits.slice(-4)}`;
  return "+91 ••••• •••••";
};

const Lock = ({ size = 14, color = "#9AA3B8" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.lock} aria-hidden="true">
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V8a4 4 0 018 0v3" />
  </svg>
);

const Field = ({ label, value, wide, mono, children, placeholder = "Not available" }) => (
  <div className={`${styles.field} ${wide ? styles.fieldWide : ""}`}>
    <div className={styles.fieldText}>
      <span className={styles.fieldLabel}>{label}</span>
      <span className={`${styles.fieldValue} ${mono ? styles.mono : ""} ${value ? "" : styles.empty}`}>{value || placeholder}</span>
    </div>
    {children || <Lock />}
  </div>
);

const ShowDetails = () => {
  const param = useParams();
  const awb = param.awbInfo;
  // A saved OTP session belongs to one AWB: drop it when this page is for another one.
  ensureOtpSessionFor(awb);
  const dispatch = useDispatch();
  usePageView("customer_details", { awb });
  const trackdata = useSelector((store) => store?.awbReducer?.data);

  const [loadState, setLoadState] = useState("loading"); // loading | ok | error
  const [copied, setCopied] = useState(false);
  const [displayOtpPage, setotpPage] = useState(false);
  const copyTimer = useRef(null);
  const hasSession = !!sessionStorage.getItem("validateotp");

  const getAwb = useCallback(async () => {
    setLoadState("loading");
    dispatch(initiateApiCall());
    const data = sessionStorage.getItem("validateotp")
      ? await verifyOtpActionForEdit(JSON.parse(sessionStorage.getItem("otp")), awb)
      : await searchAwb(awb);
    if (data?.code === "200") {
      dispatch(setCompanyInfo(data?.result?.companyInfo));
      dispatch(getAWBSuccess("data", data?.result));
      setLoadState("ok");
    } else {
      dispatch(getAWBFailure());
      setLoadState("error");
      trackError(sessionStorage.getItem("validateotp") ? "full_details" : "page_load", data, { page: "customer_details" });
    }
  }, [awb, dispatch]);

  useEffect(() => {
    getAwb();
    return () => clearTimeout(copyTimer.current);
  }, [getAwb]);

  // Without an OTP session the address is hidden. After the OTP, load the full details once.
  const loadedWithOtp = useRef(hasSession);
  useEffect(() => {
    if (!displayOtpPage && !loadedWithOtp.current && sessionStorage.getItem("validateotp")) {
      loadedWithOtp.current = true;
      getAwb();
    }
  }, [displayOtpPage, getAwb]);

  const ready = !!trackdata?.shipmentDetails;
  const shipping = trackdata?.lastestAddress?.shippingAddress;
  const locked = !shipping; // public response: no personal details
  const address = readableAddress(shipping);
  const place = [
    ["Pincode", shipping?.pinCode],
    ["City", shipping?.city],
    ["State", shipping?.state],
  ];
  const fullAddress = [address, [shipping?.city, shipping?.state].filter(Boolean).join(", "), shipping?.pinCode].filter(Boolean).join(", ");

  const copy = async () => {
    track("tp_cta_click", { cta: "copy_address", placement: "details" });
    try {
      await navigator.clipboard.writeText(fullAddress);
    } catch (e) {
      // Clipboard can be blocked (http, permissions); the label still confirms the tap.
    }
    setCopied(true);
    clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 1800);
  };

  const verify = async () => {
    track("tp_cta_click", { cta: "verify_otp", placement: "details" });
    const data = await getOTP(trackdata?.shipmentDetails?.awbnumber || awb);
    if (data?.status === "Success") {
      track("tp_otp_prompted", { trigger: "show_details", prior_session: "none" });
      setotpPage(true);
    } else trackError("otp_send", data, { trigger: "show_details" });
  };

  return (
    <div className={styles.shell}>
      <PageHeader forceLogo={loadState === "error"} />
      <main className={styles.main}>
        <div className={styles.inner}>
          {!ready && loadState === "loading" && <div className={styles.skeleton} aria-busy="true" aria-label="Loading your details" />}

          {!ready && loadState === "error" && (
            <div className={styles.errorCard}>
              <h2 className={styles.errorTitle}>We couldn't load your details</h2>
              <span className={styles.errorText}>Check your connection and try again.</span>
              <button
                type="button"
                className={styles.retry}
                onClick={() => {
                  track("tp_cta_click", { cta: "try_again", placement: "error_card" });
                  getAwb();
                }}
              >
                Try again
              </button>
            </div>
          )}

          {ready && (
            <div className={styles.card}>
              <div className={styles.content}>
                <div className={styles.head}>
                  <div className={styles.headText}>
                    <h1 className={styles.title}>Customer Details</h1>
                    <p className={styles.subtitle}>Check your delivery address and contact information</p>
                  </div>
                  <span className={styles.viewOnly}>
                    <Lock size={13} color="currentColor" />
                    View only
                  </span>
                </div>

                <section className={styles.section}>
                  <div className={styles.sectionHead}>
                    <span className={styles.sectionIcon}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7130F0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="12" cy="8" r="4" />
                        <path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" />
                      </svg>
                    </span>
                    <span className={styles.sectionTitle}>Contact</span>
                    <span className={styles.sectionRule} />
                  </div>
                  <div className={styles.two}>
                    <Field label="Name" value={locked ? "" : shipping?.name} placeholder={locked ? "Hidden" : undefined} />
                    <Field label="Phone Number" value={locked ? "" : displayPhone(shipping?.msisdn)} placeholder={locked ? "Hidden" : undefined} mono />
                  </div>
                </section>

                <section className={styles.section}>
                  <div className={styles.sectionHead}>
                    <span className={styles.sectionIcon}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7130F0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M12 21s-7-6.2-7-12a7 7 0 0114 0c0 5.8-7 12-7 12z" />
                        <circle cx="12" cy="9" r="2.5" />
                      </svg>
                    </span>
                    <span className={styles.sectionTitle}>Delivery address</span>
                    <span className={styles.sectionRule} />
                  </div>
                  <Field label="Address" value={locked ? "" : address} placeholder={locked ? "Hidden" : undefined} wide>
                    {!locked && address && (
                      <button type="button" aria-label="Copy address" className={`${styles.copy} ${copied ? styles.copied : ""}`} onClick={copy}>
                        {copied ? (
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M5 12.5l4.5 4.5L19 7.5" />
                          </svg>
                        ) : (
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <rect x="9" y="9" width="11" height="11" rx="2" />
                            <path d="M5 15V5a1 1 0 011-1h10" />
                          </svg>
                        )}
                        {copied ? "Copied" : "Copy"}
                      </button>
                    )}
                  </Field>
                  <div className={styles.three}>
                    {place.map(([label, value]) => (
                      <Field key={label} label={label} value={locked ? "" : value} placeholder={locked ? "Hidden" : undefined} mono />
                    ))}
                  </div>
                </section>

                {locked ? (
                  <div className={styles.help}>
                    <div className={styles.helpText}>
                      <Lock size={18} color="#7130F0" />
                      <span>To protect your privacy, these details show only after you verify with the OTP sent to your registered mobile number.</span>
                    </div>
                    <button type="button" className={styles.helpBtn} onClick={verify}>
                      Verify with OTP
                    </button>
                  </div>
                ) : (
                  <div className={styles.help}>
                    <div className={styles.helpText}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7130F0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.helpIcon} aria-hidden="true">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 11v5M12 8h.01" />
                      </svg>
                      <span>These details can’t be edited here. If something looks wrong, contact Blitz support before your parcel is out for delivery.</span>
                    </div>
                    <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className={styles.helpBtn} onClick={() => track("tp_cta_click", { cta: "chat_support", placement: "details" })}>
                      <span className={styles.waDot} />
                      Let's Chat Now!
                    </a>
                  </div>
                )}
              </div>

              <div className={styles.art}>
                <div className={styles.artBox}>
                  <img src={Illustration} alt="Customer profile card with a delivery location pin and lock badge" />
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {displayOtpPage && (
        <Otp
          action={"Edit2"}
          setotpPage={setotpPage}
          awb={trackdata?.shipmentDetails?.awbnumber || awb}
          subOrderId={trackdata?.orderDetail?.[0]?.suborder_id}
          orderInfo={trackdata?.orderDetail?.[0]?.order}
          close={() => setotpPage(false)}
        />
      )}
    </div>
  );
};

export default ShowDetails;
